/**
 * The OneSystem quota on the catalogue. The catalogue is priced for an empty cart; `/pricing` returns what each
 * eligible pack would cost if added to the current cart (`quotaPreview`), and this lays that answer over it.
 */

import Big from 'big.js'

import type {
  CartItem,
  QuotaPreviewEntryV2DTO,
  SessionPricingV2DTO,
  ResolvedCatalogue,
  ResolvedShellProduct,
  ResolvedVariant
} from 'models/purchaseFlowRevamp'

import type { SessionQuotaPreview } from './slices/catalogueSlice'

/** The catalogue with every previewed pack priced as adding it would price it; untouched shells keep their identity. */
export function applyQuotaPreview(
  catalogue: ResolvedCatalogue,
  preview: QuotaPreviewEntryV2DTO[] | null | undefined
): ResolvedCatalogue {
  if (!preview || preview.length === 0) return catalogue
  const byCode = new Map(preview.map((entry) => [entry.productCode, entry]))

  const overlayVariant = (variant: ResolvedVariant): ResolvedVariant => {
    const entry = byCode.get(variant.productCode)
    if (!entry || !variant.price) return variant
    return {
      ...variant,
      price: { ...variant.price, unitPrice: new Big(entry.unitPrice.amount).toFixed(2) },
      quotaCovered: entry.coveredIfAdded,
      quotaDisplacesProductCodes: entry.displacesProductCodes,
    }
  }

  const overlayShells = (shells: ResolvedShellProduct[]): ResolvedShellProduct[] => {
    let changed = false
    const next = shells.map((shell) => {
      const variants = shell.variants.map(overlayVariant)
      if (variants.every((variant, index) => variant === shell.variants[index])) return shell
      changed = true
      return { ...shell, variants }
    })
    return changed ? next : shells
  }

  const shellProducts = overlayShells(catalogue.shellProducts)
  const bundles = overlayShells(catalogue.bundles)
  if (shellProducts === catalogue.shellProducts && bundles === catalogue.bundles) return catalogue
  return { ...catalogue, shellProducts, bundles }
}

/**
 * The cart items that adding [variant] would make chargeable: the packs the preview names, plus this same
 * product's lines that are free today when one more unit would not be (the backend never lists a product
 * as displacing itself).
 */
export function displacedCartItems(variant: ResolvedVariant | null, items: CartItem[]): CartItem[] {
  if (!variant) return []
  const displaced = new Set(variant.quotaDisplacesProductCodes ?? [])
  const losesOwnCoverage = variant.quotaCovered === false
  return items.filter((item) =>
    displaced.has(item.variantId) ||
    (losesOwnCoverage && item.variantId === variant.productCode && item.priceSnapshot?.quotaCovered === true))
}

/** Charged 0 with an original to strike; a product that is simply free has no original. */
export function isQuotaFree(variant: Pick<ResolvedVariant, 'price' | 'basePrice'> | null | undefined): boolean {
  if (!variant?.price || !variant.basePrice) return false
  return new Big(variant.price.unitPrice).eq(0)
}

/** The preview entries to lay over the catalogue of [sessionId]; an answer kept for another session is ignored. */
export function quotaPreviewForSession(
  preview: SessionQuotaPreview | null,
  sessionId: string | null | undefined
): QuotaPreviewEntryV2DTO[] | null {
  return preview && preview.sessionId === sessionId ? preview.entries : null
}

/** The stored preview after a `/pricing` round: dropped with an emptied cart, replaced by a fresh answer, else kept. */
export function nextQuotaPreview(input: {
  hasLines: boolean
  data: SessionPricingV2DTO | undefined
  isStale: boolean
  sessionId: string | null | undefined
}): SessionQuotaPreview | null | 'keep' {
  if (!input.hasLines) return null
  if (!input.data?.oneSystemQuota || input.isStale || !input.sessionId) return 'keep'
  return { sessionId: input.sessionId, entries: input.data.quotaPreview ?? [] }
}
